import { Fragment } from 'react';

import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from 'lucide-react';

import { useGetIndications } from '@/hooks/api/use-get-indications';

import { FetchStates } from '@/components/fetch-states';
import { SelectField } from '@/components/form/select-field';
import { Fieldset } from '@/components/form/fieldset';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

type SelectedIndication = { id: string; indication: string; method?: string };
type DropdownSelectProps = {
  siteId?: string;
  selected?: SelectedIndication[];
  onSelect?: (value: SelectedIndication[]) => void;
  disabled?: boolean;
};

export function CaseIndicationMultiSelect(props: DropdownSelectProps) {
  const { selected = [], onSelect, disabled = false, siteId } = props;

  const { t } = useTranslation();
  const { indications, loading: loadingIndications } = useGetIndications({
    site_id: siteId,
  });

  const label =
    selected?.length > 1
      ? `${selected.length} ${t('case-create-indications-selected')}`
      : `1 ${t('case-create-indication-selected')}`;

  const handleSelect = (_indication: (typeof indications)[0]) => {
    if (disabled || !onSelect) return;

    const indicationToAdd = {
      id: _indication.id,
      indication: _indication.indication,
      method: _indication.methods?.includes('Telehealth') ? 'Telehealth' : undefined,
    };

    if (!selected) return onSelect([indicationToAdd]);

    const exists = selected?.some((item) => item.id === indicationToAdd.id);
    if (exists) {
      const newSelected = selected.filter((item) => item.id !== indicationToAdd.id);
      onSelect(newSelected);
      return;
    }

    const newSelected = [...(selected ?? []), indicationToAdd];
    onSelect(newSelected);
  };

  const handleUpdateMethod = (indication: SelectedIndication, value: string) => {
    if (disabled || !onSelect) return;

    const updateIndication = {
      ...indication,
      method: value,
    };

    const newSelected = selected.map((s) => {
      if (s.id === indication.id) {
        return updateIndication;
      }
      return s;
    });

    onSelect(newSelected);
  };

  if (loadingIndications) return <FetchStates loading />;

  if (!indications.length) return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Fieldset>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                disabled={disabled}
                variant="outline-muted"
                className="w-full mt-3 justify-between"
              >
                {selected?.length === 0 ? t('default-select') : label}
                <ChevronDownIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="md:min-w-60 flex flex-col">
              <FetchStates loading={loadingIndications} empty={indications.length === 0}>
                {indications.map((indication, index) => (
                  <Fragment key={indication.id}>
                    <DropdownMenuCheckboxItem
                      key={indication.id}
                      disabled={disabled}
                      checked={selected?.some((s) => s.id === indication.id)}
                      onCheckedChange={() => handleSelect(indication)}
                    >
                      {indication.indication}
                    </DropdownMenuCheckboxItem>
                    {index < indications.length - 1 && <DropdownMenuSeparator />}
                  </Fragment>
                ))}
              </FetchStates>
            </DropdownMenuContent>
          </DropdownMenu>
        </Fieldset>
      </div>
      {selected.map((indication) => (
        <div key={indication.id} className="flex flex-wrap justify-between items-center gap-4 mt-1">
          <div className="flex-1">{indication.indication}</div>
          <div className="lg:basis-52">
            <SelectField
              required
              disabled={disabled}
              enableEmptyOption
              label={t('case-create-indication-method')}
              options={indications.find((i) => i.id === indication.id)?.methods ?? []}
              name="method"
              value={indication.method}
              onChange={({ target: { value } }) => handleUpdateMethod(indication, value)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
